/**
 * Runtime glue for ambient frogs: pose art plus persistent per-yard state.
 *
 * Each yard keeps two independent frog states across Friends switches.
 */

import {
  cancelFrogHeadSequence,
  createFrogState,
  type FrogInstanceConfig,
  type FrogPose,
  type FrogState,
  type FrogStepContext,
} from '@/world-prototype/creatures/frogBehavior';
import {
  PHYSICAL_SIZE_FEET,
  READABILITY,
} from '@/world-prototype/scale/worldScale';

// Lowercase `frog` — matches the poses folder for Linux / GitHub Pages.
const FROG_POSE_DIR = `${import.meta.env.BASE_URL}art/animals/frog/poses`;

export const FROG_POSE_SRC: Readonly<Record<FrogPose, string>> = {
  sit: `${FROG_POSE_DIR}/frog-sit.png`,
  hop: `${FROG_POSE_DIR}/frog-hop.png`,
  sleep: `${FROG_POSE_DIR}/frog-sleep.png`,
};

export const FROG_POSE_IMAGE = {
  imageWidth: 1294,
  imageHeight: 1245,
} as const;

/** Sitting body height on the shared pose canvas (px). */
const FROG_SIT_BODY_PX = 1192;

/** Small extra display bump — keeps frogs tappable without feeling oversized. */
const FROG_DISPLAY_READABILITY = 1.05;

/**
 * Display height in feet. Sized ~45% of the rabbit so frogs read as the smaller
 * animal while staying tappable on a phone.
 */
export const FROG_CANVAS_FEET =
  PHYSICAL_SIZE_FEET.frog *
  READABILITY.frog *
  FROG_DISPLAY_READABILITY *
  (FROG_POSE_IMAGE.imageHeight / FROG_SIT_BODY_PX);

const yardStates = new Map<string, FrogState[]>();
const headPerchLocks = new Map<string, string | null>();

export function tryAcquireFrogHeadPerch(
  yardId: string,
  frogId: string,
): boolean {
  if (headPerchLocks.get(yardId)) return false;
  headPerchLocks.set(yardId, frogId);
  return true;
}

export function releaseFrogHeadPerch(yardId: string, frogId: string): void {
  if (headPerchLocks.get(yardId) === frogId) {
    headPerchLocks.set(yardId, null);
  }
}

export function frogHeadPerchCallbacks(yardId: string): Pick<
  FrogStepContext,
  'tryAcquireHeadPerch' | 'releaseHeadPerch'
> {
  return {
    tryAcquireHeadPerch: (frogId) => tryAcquireFrogHeadPerch(yardId, frogId),
    releaseHeadPerch: (frogId) => releaseFrogHeadPerch(yardId, frogId),
  };
}

export function cancelFrogHeadSequencesForYard(
  yardId: string,
  states: readonly FrogState[],
  ctx: FrogStepContext,
): void {
  headPerchLocks.set(yardId, null);
  for (const state of states) {
    cancelFrogHeadSequence(state, ctx);
  }
}

export function frogStatesForYard(
  yardId: string,
  instances: readonly FrogInstanceConfig[],
): FrogState[] {
  let states = yardStates.get(yardId);
  if (!states) {
    states = instances.map((instance) => createFrogState(instance));
    yardStates.set(yardId, states);
  }
  return states;
}

export function resetFrogStatesForTests(): void {
  yardStates.clear();
  headPerchLocks.clear();
}
